/**
 * MultiSelect component — shared types and Zod prop schema.
 *
 * A multi-choice selection list for intake flows and questionnaires.
 * Renders as a vertical list of tappable option cards with checkbox semantics.
 * Reuses the option schema and design tokens from SingleSelect.
 *
 * Source of truth: Pangea Galapagos UI Design System — Beta
 * https://www.figma.com/design/Orc6uUodQaoXep59CGaUNw
 */

import { z } from 'zod';
import { spacing } from '../../elements/spacing';
import { optionSchema } from '../SingleSelect/SingleSelect';

/**
 * A MultiSelect option. `subOptions` is a single choice that appears directly
 * under the option, indented, while it is selected; its values share the
 * MultiSelect's `value` array, so every value in the tree must be unique.
 * Web only: native ignores `subOptions`.
 */
export const multiSelectOptionSchema = optionSchema.extend({
  subOptions: z.array(optionSchema).optional(),
  /** Accessible name of the sub-option group. */
  subOptionsLabel: z.string().optional(),
});

export type MultiSelectOption = z.infer<typeof multiSelectOptionSchema>;

/** How far a sub-option group sits in from its parent's left edge. */
export const SUB_OPTIONS_INDENT = spacing.lg;

/**
 * MultiSelect prop schema for the catalog (ADR-011).
 *
 * `onSelect` is a runtime callback defined on platform-specific interfaces
 * (`NativeMultiSelectProps`, `WebMultiSelectProps`), not in this catalog
 * schema. Catalog schemas define data/variant props only (ADR-011 convention).
 */
export const multiSelectPropsSchema = z.object({
  label: z.string(),
  /** Suppress the visible section label; keep it as the group's accessible name. */
  hideLabel: z.boolean().default(false),
  /**
   * Whether a selected option paints the primary-coloured border. Default true
   * (selection shows a coloured outline). Set false for surfaces that convey
   * selection by the tick indicator alone (e.g. the motivation screen); the
   * option keeps a constant-width neutral divider so layout never shifts.
   */
  highlightSelectedBorder: z.boolean().default(true),
  value: z.array(z.string()).optional(),
  options: z.array(multiSelectOptionSchema),
  disabled: z.boolean().optional(),
});

export type MultiSelectProps = z.infer<typeof multiSelectPropsSchema>;

export function normalizeMultiSelectValue(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.filter((item): item is string => typeof item === 'string');
  }
  if (typeof value === 'string') return [value];
  return [];
}

/**
 * The value after the member presses `option` (a top-level option). Selecting
 * adds it; deselecting removes it and any of its sub-options, so a sub-choice
 * never outlives its parent. Shared so the web build and its tests agree.
 */
export function toggleMultiSelectOption(
  current: readonly string[],
  option: MultiSelectOption,
): string[] {
  if (!current.includes(option.value)) return [...current, option.value];
  const children = new Set((option.subOptions ?? []).map((sub) => sub.value));
  return current.filter((value) => value !== option.value && !children.has(value));
}

/** The value after picking `subValue` under `parent`: one sub-option per parent. */
export function selectMultiSelectSubOption(
  current: readonly string[],
  parent: MultiSelectOption,
  subValue: string,
): string[] {
  const siblings = new Set((parent.subOptions ?? []).map((sub) => sub.value));
  const kept = current.filter((value) => !siblings.has(value));
  return kept.includes(parent.value) ? [...kept, subValue] : [...kept, parent.value, subValue];
}

/** The selected sub-option of `parent`, or null when none is selected. */
export function selectedMultiSelectSubOption(
  current: readonly string[],
  parent: MultiSelectOption,
): string | null {
  return parent.subOptions?.find((sub) => current.includes(sub.value))?.value ?? null;
}
